import { Dialog as DialogHeadless } from '@headlessui/react'
import { Button as HeroButton, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from '@heroui/react'
import { MagnifyingGlassIcon, StarFilledIcon } from '@radix-ui/react-icons'
import { useMutation } from '@tanstack/react-query'
import {
  Badge,
  Button,
  IconButton,
  Tabs,
  TextField,
  Tooltip,
} from '@radix-ui/themes'
import ActionBar from 'components/core/ActionBar'
import { PageTitle } from 'components/core/PageTitle'
import { PageWrapper } from 'components/core/PageWrapper'
import config from 'config'
import {
  AU,
  DE,
  ES,
  FR,
  GB,
  IT,
  NL,
  SE,
  US,
} from 'country-flag-icons/react/3x2'
import { useAuth } from 'hooks/useAuth'
import { useVoices } from 'hooks/useVoices'
import { debounce } from 'lodash'
import {
  Bookmark,
  Copy,
  ExternalLink,
  PlayIcon,
  Plus,
  Trash,
  Upload,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { AiOutlineLoading } from 'react-icons/ai'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'
import { getOrgId } from 'utils/funcs/browser/getOrgId'
import blandlogo from './../../../assets/brand/bland-b.svg'
import CreateVoiceCloneModal from './CreateVoiceCloneModal'
import PixelAvatar from './PixelAvatar'
import VoiceStudio from './VoiceStudio'

const languages = [
  'english',
  'italian',
  'french',
  'german',
  'spanish',
  'british',
  'australian',
  'swedish',
  'dutch',
  'american',
  'filipino',
]

const LANGUAGE_ICONS = {
  english: <US style={{ width: 14, height: 14 }} />,
  italian: <IT style={{ width: 14, height: 14 }} />,
  french: <FR style={{ width: 14, height: 14 }} />,
  german: <DE style={{ width: 14, height: 14 }} />,
  spanish: <ES style={{ width: 14, height: 14 }} />,
  british: <GB style={{ width: 14, height: 14 }} />,
  australian: <AU style={{ width: 14, height: 14 }} />,
  swedish: <SE style={{ width: 14, height: 14 }} />,
  dutch: <NL style={{ width: 14, height: 14 }} />,
  american: <US style={{ width: 14, height: 14 }} />,
  filipino: <span style={{ fontSize: 10 }}>🇵🇭</span>,
}

function VoicesTable({
  title,
  description,
  data,
  api_key,
  ttsText = 'Hey this is Blandie',
  personal = false,
  refetchVoices,
}) {
  const [rawInput, setRawInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [delVoiceId, setDelVoiceId] = useState(null)
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure({
    onClose: () => {
      setDelVoiceId(null)
    },
  })
  const { refetchUser } = useAuth()

  // Create a debounced function that updates the searchQuery from rawInput

  const [searchInput, setSearchInput] = useState('')
  const [isLoading, setIsLoading] = useState({
    isLoading: false,
    id: undefined,
  })
  const [saveLoading, setSaveLoading] = useState({
    isLoading: false,
    id: undefined,
  })
  const [animatingVoiceId, setAnimatingVoiceId] = useState(null)
  const orgId = getOrgId()

  const getVoiceGender = useCallback((voice) => {
    try {
      const description = voice?.description?.toLowerCase() || ''
      const tags = voice?.tags?.map(tag => tag.toLowerCase()) || []

      if (description.includes('female') || tags.includes('female'))
        return 'Female'
      if (description.includes('male') || tags.includes('male'))
        return 'Male'
      return undefined
    }
    catch {
      return undefined
    }
  }, [])

  const getVoiceLanguage = useCallback((voice) => {
    try {
      const languageTag = voice.tags
        .map(tag => tag.toLowerCase())
        .find(tag => languages.includes(tag))

      const languageInDescription = languages.find(lang =>
        voice.description.toLowerCase().includes(lang.toLowerCase()),
      )

      const foundLanguage = languageTag || languageInDescription

      return foundLanguage ? LANGUAGE_ICONS[foundLanguage.toLowerCase()] : null
    }
    catch {
      return null
    }
  }, [])

  const fetchTTSAudio = async (voiceId) => {
    try {
      setIsLoading({
        isLoading: true,
        id: voiceId,
      })

      const response = await fetch(
        `${config.API_URL}/v1/voices/${voiceId}/sample`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': api_key,
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
          body: JSON.stringify({
            text: ttsText,
            response_type: 'file', // Change to 'file' for consistent format
          }),
        },
      )

      if (response.status === 429) {
        setIsLoading({ isLoading: false, id: undefined })
        return toast.error('Rate limit exceeded. Please try again later.')
      }

      let latency = null
      if (response.ok) {
        latency = response.headers.get('x-latency')
      }

      const blob = await response.blob()
      const audioUrl = URL.createObjectURL(blob)
      const audio = new Audio(audioUrl)

      audio.play().catch((err) => {
        alert(`Audio Error: ${err}`)
      })

      setIsLoading({ isLoading: false, id: undefined })
      setTimeout(() => setAnimatingVoiceId(null), 3000)
      return audio
    }
    catch (error) {
      console.error('Failed to fetch TTS audio:', error)
      setIsLoading({ isLoading: false, id: undefined })
      setAnimatingVoiceId(null)
    }
  }

  const addToLibrary = async (voiceId) => {
    try {
      setSaveLoading({
        isLoading: true,
        id: voiceId,
      })
      const response = await fetch(
        `${config.API_URL}/v1/voices/library/add/${voiceId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': api_key,
            ...(orgId && { 'x-bland-org-id': orgId }),
          },
        },
      )

      if (response.ok) {
        toast.success(
          'Voice added to library. Please refresh the page to view it in your library.',
        )
      }
      else {
        toast.error('Failed to add voice to library.')
      }

      setSaveLoading({
        isLoading: false,
        id: undefined,
      })
    }
    catch (error) {
      setSaveLoading({
        isLoading: false,
        id: undefined,
      })
      console.error('Failed to add voice to library:', error)
    }
  }

  const preprocessedData = useMemo(() => {
    return data?.map((item) => {
      const searchFields = [
        item.name?.toLowerCase() || '',
        item.description?.toLowerCase() || '',
        item.id?.toLowerCase() || '',
        ...(item.tags?.map(tag => tag.toLowerCase()) || []),
      ]
      const searchString = searchFields.join(' ')
      return { ...item, __searchString: searchString }
    })
  }, [data])

  const filteredData = useMemo(() => {
    if (!searchInput)
      return preprocessedData
    const searchLower = searchInput.toLowerCase()
    return preprocessedData.filter(item =>
      item.__searchString.includes(searchLower),
    )
  }, [searchInput, preprocessedData])

  const ITEMS_PER_PAGE = 20
  const VIRTUALIZATION_BUFFER = 5

  const deleteVoiceMutation = useMutation({
    mutationFn: async (voiceId) => {
      const response = await $fetch(`/v1/voices/${voiceId}`, {
        method: 'DELETE',
      })
      return response
    },
    onSuccess: () => {
      toast.success('Voice removed successfully!')
      refetchVoices()
      refetchUser()
      setDelVoiceId(null)
      onClose()
    },
    onError: (error) => {
      console.error(error)
      toast.error('Error deleting voice. Please try again later.')
    },
  })

  const handleDeleteVoice = () => {
    if (delVoiceId) {
      deleteVoiceMutation.mutate(delVoiceId)
    }
  }

  const paginationInfo = useMemo(() => {
    const totalPages = Math.ceil(filteredData?.length / ITEMS_PER_PAGE)
    return {
      totalPages,
      totalItems: filteredData?.length,
    }
  }, [filteredData])

  const pageNumbers = useMemo(() => {
    const { totalPages } = paginationInfo
    const maxPagesToShow = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1)
    }

    return Array.from(
      { length: Math.min(endPage - startPage + 1, maxPagesToShow) },
      (_, i) => startPage + i,
    )
  }, [currentPage, paginationInfo.totalPages])

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchInput(value)
        setCurrentPage(1)
      }, 750),
    [setSearchInput, setCurrentPage],
  )

  useEffect(() => {
    debouncedSearch(rawInput)
  }, [rawInput])

  const [renderedData, setRenderedData] = useState([])

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE + VIRTUALIZATION_BUFFER
    const virtualizedData = filteredData?.slice(startIndex, endIndex)
    setRenderedData(virtualizedData)
  }, [filteredData, currentPage])

  return (
    <>
      <div className="mb-2.5">
        <h2>
          {title}
          {' '}
          (
          {paginationInfo.totalItems || 0}
          )
        </h2>
        <p style={{ fontWeight: '400', color: 'GrayText' }}>{description}</p>
      </div>
      <TextField.Root
        value={rawInput}
        onChange={event => setRawInput(event.target.value)}
        className="w-full mb-4 focus:outline-none"
        placeholder="Search Voices..."
      >
        <TextField.Slot>
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th />
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {renderedData?.map((item, key) => (
            <tr key={key} className="border-b border-gray-200 row-wrapper">
              <td className="p-4 py-5 border-r border-gray-100 border-dashed">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    columnGap: 18,
                  }}
                >
                  <PixelAvatar
                    name={item?.name}
                    isAnimating={animatingVoiceId === item.id}
                  />
                  <div style={{ overflow: 'hidden' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        columnGap: 6,
                      }}
                    >
                      <p
                        style={{ fontSize: 14, fontWeight: '500' }}
                        onMouseEnter={() => {
                          setAnimatingVoiceId(item.id)
                        }}
                        onMouseLeave={() => {
                          setAnimatingVoiceId(null)
                        }}
                      >
                        {item?.name}
                      </p>

                      {getVoiceLanguage(item) && <>{getVoiceLanguage(item)}</>}
                      {getVoiceGender(item) && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            columnGap: 6,
                          }}
                        >
                          <div
                            style={{
                              height: 14,
                              background: '#f3f3f3',
                              width: 2,
                            }}
                          />
                          <p
                            style={{
                              fontSize: 10.5,
                              fontWeight: '450',
                              color: 'gray',
                            }}
                          >
                            {getVoiceGender(item)}
                          </p>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'center',
                        columnGap: 6,
                      }}
                    >
                      {item?.description && (
                        <p
                          style={{
                            fontSize: 10.5,
                            fontWeight: '400',
                            color: 'GrayText',
                          }}
                        >
                          {item?.description}
                        </p>
                      )}

                      {item?.average_rating && item.description && (
                        <div
                          style={{
                            height: 14,
                            background: '#f3f3f3',
                            width: 2,
                          }}
                        />
                      )}

                      {item?.average_rating && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <StarFilledIcon
                            color="gold"
                            style={{
                              height: 10.5,
                              width: 10.5,
                              marginRight: 2,
                            }}
                          />
                          <p
                            style={{
                              fontSize: 10.5,
                              fontWeight: '400',
                              color: 'GrayText',
                            }}
                          >
                            {Number(item?.average_rating)?.toFixed(1)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginLeft: 'auto' }}>
                    <Tooltip
                      content="Copy voice ID to clipboard"
                      side="top"
                      align="start"
                      dir="rtl"
                    >
                      <Copy
                        onClick={() => {
                          window.navigator.clipboard.writeText(item.id)
                          return toast.success('Copied voice ID to clipboard.')
                        }}
                        style={{ cursor: 'pointer' }}
                        size={15}
                        color="gray"
                      />
                    </Tooltip>
                  </div>
                  {personal && (
                    <div>
                      <HeroButton
                        color="danger"
                        variant="flat"
                        size="sm"
                        isIconOnly
                        onPress={(event) => {
                          onOpen(event)
                          setDelVoiceId(item.id)
                        }}
                      >
                        <Trash color="red" size={14} />
                      </HeroButton>
                    </div>
                  )}
                </div>
              </td>
              <td className="p-4 border-r border-gray-100 border-dashed">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {item.tags?.filter(tag => tag !== 'cloned')?.map((tag, k) => (
                    <Badge key={k} color={tag === 'Beige Clone' ? 'gold' : 'iris'} variant="soft" size="2">
                      {tag === 'Beige Clone'
                        ? (
                            <>
                              <img alt="logo" src={blandlogo} className="size-2.5 opacity-60" />
                              <p className="font-taurus font-bold tracking-tighter">Beige</p>
                            </>
                          )
                        : (
                            tag
                          )}
                    </Badge>
                  ))}
                </div>
              </td>
              <td className="p-4">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {!personal && (
                    <Tooltip
                      content="Save voice to your library"
                      side="bottom"
                      align="start"
                    >
                      <IconButton
                        loading={
                          saveLoading.isLoading && saveLoading.id === item.id
                        }
                        onClick={() => addToLibrary(item.id)}
                        color="violet"
                        size="1"
                        variant="ghost"
                      >
                        <Bookmark size={16} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Button
                    onMouseEnter={() => {
                      setAnimatingVoiceId(item.id)
                    }}
                    onMouseLeave={() => {
                      setAnimatingVoiceId(null)
                    }}
                    loading={isLoading.isLoading && isLoading.id === item.id}
                    type="button"
                    onClick={() => fetchTTSAudio(item.id)}
                    variant="soft"
                    color="gray"
                    size="1"
                    style={{ cursor: 'pointer' }}
                  >
                    Play
                    <PlayIcon size={10} strokeWidth={3} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {paginationInfo.totalPages > 1 && (
        <div className="flex justify-center items-center mt-2.5 space-x-1.5">
          <Button
            variant="soft"
            color="gray"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            size="1"
          >
            Previous
          </Button>

          {pageNumbers?.map(number => (
            <Button
              key={number}
              variant={currentPage === number ? 'surface' : 'soft'}
              color={currentPage === number ? 'green' : 'gray'}
              onClick={() => setCurrentPage(number)}
              size="1"
            >
              {number}
            </Button>
          ))}

          <Button
            variant="soft"
            size="1"
            color="gray"
            onClick={() =>
              setCurrentPage(prev =>
                Math.min(paginationInfo.totalPages, prev + 1),
              )}
            disabled={currentPage === paginationInfo.totalPages}
          >
            Next
          </Button>
        </div>
      )}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} radius="sm" size="3xl">
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="pb-0">
                <div className="text-2xl">
                  Delete Voice
                </div>
              </ModalHeader>
              <ModalBody>
                <div style={{ fontSize: 14, fontWeight: '450' }}>Are you sure you want to delete this voice? This action cannot be undone.</div>
              </ModalBody>
              <ModalFooter>
                <HeroButton color="grey" variant="flat" onPress={onClose}>
                  Cancel
                </HeroButton>
                <HeroButton
                  color="danger"
                  type="button"
                  onPress={handleDeleteVoice}
                  loading={deleteVoiceMutation.isPending}
                  disabled={deleteVoiceMutation.isPending}
                >
                  {deleteVoiceMutation.isPending
                    ? (
                        <div className="flex items-center">
                          <AiOutlineLoading className="animate-spin mr-2" />
                          <span>Deleting Voice...</span>
                        </div>
                      )
                    : (
                        'Delete'
                      )}
                </HeroButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export default function Voices() {
  const { user: { user: lsUser }, org } = useAuth()
  const { data: voices, refetch: refetchVoices } = useVoices()
  const api_key = getAuthToken(false)
  const [tab, setTab] = useState('curated')
  const [showCustomizeModal, setShowCustomizeModal] = useState(false)
  const [tempSampleText, setTempSampleText] = useState(
    'Hey this is Blandie, can you hear me alright?',
  )

  const privateVoicesList = useMemo(
    () =>
      voices?.filter(
        voice =>
          voice.user_id !== null
          && (voice.user_id === lsUser?.id || voice.user_id === org?.id)
          && !voice.name.includes('Public -'),
      ),
    [voices],
  )

  const curatedVoicesList = useMemo(
    () =>
      voices?.filter(
        voice =>
          voice?.tags?.includes('Bland Curated') && voice?.user_id == null,
      ),
    [voices],
  )

  const handleTabChange = (value) => {
    if (value === 'docs') {
      setTab('studio')
      return
    }
    setTab(value)
  }

  return (
    <PageWrapper padding="70px 30px 0px 84px">
      <ActionBar top spaceBetween>
        <PageTitle>Voices</PageTitle>
        <CreateVoiceCloneModal>
          {({ onPress }) => (
            <HeroButton
              color="primary"
              size="sm"
              radius="sm"
              variant="solid"
              onPress={onPress}
            >
              <Plus size={14} />
              Create new voice
            </HeroButton>
          )}
        </CreateVoiceCloneModal>
      </ActionBar>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1 className="font-semibold">Voice Library</h1>
        {tab !== 'studio' && (
          <Button
            variant="soft"
            type="button"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowCustomizeModal(true)}
          >
            Customize TTS Text
          </Button>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <Tabs.Root value={tab} onValueChange={handleTabChange}>
          <Tabs.List color="gray" highContrast className="mb-6">
            <Tabs.Trigger value="curated" style={{ cursor: 'pointer' }}>
              Curated Voices
            </Tabs.Trigger>
            <Tabs.Trigger value="account" style={{ cursor: 'pointer' }}>
              Your Voices
            </Tabs.Trigger>
            <Tabs.Trigger value="all" style={{ cursor: 'pointer' }}>
              All Voices
            </Tabs.Trigger>
            <Tabs.Trigger value="studio" style={{ cursor: 'pointer' }}>
              Voice Studio
            </Tabs.Trigger>
            <Tabs.Trigger
              value="docs"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault()
                window.open('https://docs.bland.ai/api-v1/post/speak', '_blank')
              }}
            >
              TTS Docs
              {' '}
              <ExternalLink size={12} className="ml-1" />
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="account">
            <VoicesTable
              ttsText={tempSampleText}
              title="Cloned & Saved Voices"
              description="Private cloned voices and saved voice library."
              api_key={api_key}
              data={privateVoicesList}
              personal
              refetchVoices={refetchVoices}
            />
          </Tabs.Content>
          <Tabs.Content value="curated">
            <VoicesTable
              ttsText={tempSampleText}
              title="Bland Curated Voices"
              description="Handpicked voices tailored for profressional use cases."
              api_key={api_key}
              data={curatedVoicesList}
              refetchVoices={refetchVoices}
            />
          </Tabs.Content>
          <Tabs.Content value="all">
            <VoicesTable
              ttsText={tempSampleText}
              title="All Public Voices"
              description="All available public voices, including cloned voices."
              api_key={api_key}
              data={
                voices?.filter(
                  item => item?.user_id !== org?.id || !item?.user_id,
                ) || []
              }
              refetchVoices={refetchVoices}
            />
          </Tabs.Content>
          <Tabs.Content value="studio">
            <VoiceStudio />
          </Tabs.Content>
        </Tabs.Root>
      </div>

      <DialogHeadless
        open={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <DialogHeadless.Overlay className="fixed inset-0 bg-black opacity-30" />
          <div className="relative bg-white rounded-md w-[400px] mx-2.5 p-4 shadow-xl">
            <DialogHeadless.Title className="text-sm font-medium text-gray-900 mb-2.5">
              Customize TTS Sample Text
            </DialogHeadless.Title>
            <textarea
              value={tempSampleText}
              onChange={e => setTempSampleText(e.target.value)}
              className="w-full h-24 p-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-2.5"
              placeholder="Enter your custom text here..."
            />
            <div className="flex justify-end space-x-2.5">
              <button
                type="button"
                onClick={() => setShowCustomizeModal(false)}
                className="px-2.5 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-300 ease-in-out"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowCustomizeModal(false)}
                className="px-2.5 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition duration-300 ease-in-out"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </DialogHeadless>
    </PageWrapper>
  )
}
